/**
 * Distributed Tracing + Correlation Utilities
 *
 * Attaches Sentry trace context AND the host's hierarchical correlation IDs
 * (machine_id, broadcast_id) to outbound websocket messages. Delegates the
 * mechanics to the shared `@playcastdotio/Observability` helpers so every
 * surface stamps messages the same way; this module only supplies the
 * host-specific correlation source.
 */

import * as Sentry from '@sentry/react';
import type { SpanAttributes } from '@sentry/core';
import {
  attachObservabilityToMessage,
  type CorrelatableMessage,
} from '@playcastdotio/Observability';
import { getHostCorrelationIds } from './hostCorrelation';

/**
 * Attach the active Sentry trace + the host's correlation IDs to a websocket
 * message. Name retained for backward compatibility with existing call sites;
 * it now stamps correlation IDs in addition to the trace headers.
 *
 * @param message - The websocket message to enrich (mutated in place)
 * @returns The same message reference
 */
export function attachSentryTraceToMessage<T extends CorrelatableMessage>(message: T): T {
  return attachObservabilityToMessage(message, getHostCorrelationIds());
}

/**
 * Create a Sentry span and attach trace + correlation to a websocket message.
 * Use this when you want to both track the operation and propagate context.
 *
 * @param spanName - Name of the Sentry span
 * @param operation - Operation type (e.g., 'websocket', 'matchmaking', 'connection')
 * @param attributes - Optional span attributes for context
 * @param fn - Function that sends the message (receives the enriched message)
 * @param message - The message to enrich and send
 */
export function sendWithTracing<T extends CorrelatableMessage>(
  spanName: string,
  operation: string,
  attributes: SpanAttributes,
  fn: (message: T) => void,
  message: T,
): void {
  Sentry.startSpan(
    {
      name: spanName,
      op: operation,
      attributes,
    },
    () => {
      attachSentryTraceToMessage(message);
      fn(message);
    },
  );
}
